import { queryDatabaseHoles } from '../../services/drillingApi';
import {
  cacheQueryKeys,
  holeQueryCacheId,
  readCachedQueryKeys,
} from '../../store/holeOfflineCache';
import {
  displayKeyFromRow,
  hydrateHoleRecordStore,
  keyFromRow,
  markCollarMisses,
  missingKeys,
  putAssays,
  putCollars,
  putGeology,
  putSurveys,
} from '../../store/holeRecordStore';

export const HOLE_PAGE_SIZE = 2000;

export function chunkKeys(keys, size = HOLE_PAGE_SIZE) {
  const out = [];
  for (let i = 0; i < (keys || []).length; i += size) {
    out.push(keys.slice(i, i + size));
  }
  return out;
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableError = (error) => {
  const errorMessage = error?.message || error?.toString() || String(error) || '';
  const errorString = errorMessage.toLowerCase();
  const errorName = (error?.name || '').toLowerCase();
  const errorType = (error?.type || '').toLowerCase();
  return (
    errorString.includes('http2') ||
    errorString.includes('protocol_error') ||
    errorString.includes('failed to fetch') ||
    errorString.includes('network') ||
    errorString.includes('aborted') ||
    errorString.includes('timeout') ||
    errorName.includes('http2') ||
    errorName.includes('network') ||
    errorName.includes('abort') ||
    errorType === 'networkerror' ||
    errorType === 'abort'
  );
};

function isOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

export async function fetchQueryHolesWithRetry(params, attempt = 0) {
  try {
    return await queryDatabaseHoles(params);
  } catch (error) {
    if (isOffline()) throw error;
    if (isRetryableError(error) && attempt < 10) {
      const backoffDelay = Math.min(2 ** Math.min(attempt, 5) * 1000, 30000);
      await delay(backoffDelay);
      return fetchQueryHolesWithRetry(params, attempt + 1);
    }
    throw error;
  }
}

function progressPercent(phaseStart, phaseSpan, loaded, total) {
  if (!total) return phaseStart;
  return phaseStart + (phaseSpan * loaded) / total;
}

export async function fetchAllHoleKeys({ dataset, filters, isplan, onProgress, isCancelled }) {
  await hydrateHoleRecordStore();
  const cacheId = holeQueryCacheId({ dataset, filters, isplan });
  const keys = [];
  let offset = 0;
  let total = null;
  try {
    while (true) {
      if (isCancelled?.()) return keys;
      const page = await fetchQueryHolesWithRetry({
        dataset,
        filters,
        isplan,
        stage: 'keys',
        offset,
        pageSize: HOLE_PAGE_SIZE,
      });
      if (total == null) total = Number(page.total ?? page.count ?? 0);
      const rows = Array.isArray(page.keys) ? page.keys : [];
      rows.forEach((row) => {
        if (row?.DataSet && row?.Hole_ID) keys.push({ DataSet: row.DataSet, Hole_ID: row.Hole_ID });
      });
      onProgress?.({
        phase: 'keys',
        loaded: keys.length,
        total: total || keys.length,
        percent: progressPercent(0, 10, keys.length, total || keys.length),
        indeterminate: !total,
      });
      if (rows.length < HOLE_PAGE_SIZE) break;
      if (total && keys.length >= total) break;
      offset += rows.length;
    }
    if (keys.length) cacheQueryKeys(cacheId, keys);
    return keys;
  } catch (error) {
    const cached = await readCachedQueryKeys(cacheId);
    if (cached?.length) {
      onProgress?.({
        phase: 'keys',
        loaded: cached.length,
        total: cached.length,
        percent: 10,
        indeterminate: false,
        cached: true,
      });
      return cached;
    }
    throw error;
  }
}

async function fetchKeyedStage(stage, keys, { dataset, filters, isplan }) {
  if (!keys.length) return {};
  return fetchQueryHolesWithRetry({
    dataset,
    filters,
    isplan,
    stage,
    keys,
    pageSize: keys.length,
  });
}

export async function loadMissingCollars(keys, ctx) {
  const miss = missingKeys(keys, 'collar');
  if (!miss.length) {
    ctx.onProgress?.({ phase: 'traces', loaded: 1, total: 1, percent: 40, indeterminate: false });
    return;
  }
  const chunks = chunkKeys(miss);
  for (let i = 0; i < chunks.length; i += 1) {
    if (ctx.isCancelled?.()) return;
    try {
      const page = await fetchKeyedStage('collars', chunks[i], ctx);
      const collars = page.collars || page.data || [];
      putCollars(collars);
      markCollarMisses(chunks[i]);
      ctx.onCollars?.(collars);
    } catch (error) {
      if (!isOffline() && !isRetryableError(error)) throw error;
    }
    ctx.onProgress?.({
      phase: 'traces',
      loaded: i + 1,
      total: chunks.length,
      percent: progressPercent(10, 30, i + 1, chunks.length || 1),
      indeterminate: false,
    });
  }
}

export async function loadMissingSurveys(keys, ctx) {
  const miss = missingKeys(keys, 'surveys');
  if (!miss.length) {
    ctx.onProgress?.({ phase: 'surveys', loaded: 1, total: 1, percent: 60, indeterminate: false });
    return;
  }
  const chunks = chunkKeys(miss);
  for (let i = 0; i < chunks.length; i += 1) {
    if (ctx.isCancelled?.()) return;
    try {
      const page = await fetchKeyedStage('surveys', chunks[i], ctx);
      putSurveys(chunks[i], page.surveys || []);
      ctx.onSurveys?.(chunks[i]);
    } catch (error) {
      if (!isOffline() && !isRetryableError(error)) throw error;
    }
    ctx.onProgress?.({
      phase: 'surveys',
      loaded: i + 1,
      total: chunks.length,
      percent: progressPercent(40, 20, i + 1, chunks.length || 1),
      indeterminate: false,
    });
  }
}

export async function loadMissingAssays(keys, ctx) {
  const miss = missingKeys(keys, 'assays');
  if (!miss.length) {
    ctx.onProgress?.({ phase: 'assays', loaded: 1, total: 1, percent: 80, indeterminate: false });
    return;
  }
  const chunks = chunkKeys(miss);
  for (let i = 0; i < chunks.length; i += 1) {
    if (ctx.isCancelled?.()) return;
    try {
      const page = await fetchKeyedStage('assays', chunks[i], ctx);
      putAssays(chunks[i], page.assays || []);
      ctx.onAssays?.(chunks[i]);
    } catch (error) {
      if (!isOffline() && !isRetryableError(error)) throw error;
    }
    ctx.onProgress?.({
      phase: 'assays',
      loaded: i + 1,
      total: chunks.length,
      percent: progressPercent(60, 20, i + 1, chunks.length || 1),
      indeterminate: false,
    });
  }
}

export async function loadMissingGeology(keys, ctx) {
  const miss = missingKeys(keys, 'geology');
  if (!miss.length) {
    ctx.onProgress?.({ phase: 'geology', loaded: 1, total: 1, percent: 95, indeterminate: false });
    return;
  }
  const chunks = chunkKeys(miss);
  for (let i = 0; i < chunks.length; i += 1) {
    if (ctx.isCancelled?.()) return;
    try {
      const page = await fetchKeyedStage('geology', chunks[i], ctx);
      putGeology(chunks[i], page.geology || []);
      ctx.onGeology?.(chunks[i]);
    } catch (error) {
      if (!isOffline() && !isRetryableError(error)) throw error;
    }
    ctx.onProgress?.({
      phase: 'geology',
      loaded: i + 1,
      total: chunks.length,
      percent: progressPercent(80, 15, i + 1, chunks.length || 1),
      indeterminate: false,
    });
  }
}

export { displayKeyFromRow, keyFromRow };
